import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { orderService, MesaResumo, Order } from '../../services';
import { STATUS_LABELS } from './Orders';
import TempoDeEspera from '../../components/TempoDeEspera';
import ModalMotivoCancelamento from '../../components/ModalMotivoCancelamento';
import ModalConfirmacao from '../../components/ModalConfirmacao';

/**
 * Aba "Comandas": gestão das mesas — comandas abertas por mesa, detalhe de
 * cada comanda (itens com origem e valores), cancelar item individual ou a
 * comanda inteira, e histórico das fechadas de hoje (admin).
 */
export default function Comandas() {
  const { perfilRestaurante, user } = useAuth();
  const navigate = useNavigate();
  const ehAdmin = perfilRestaurante === 'SUPER_ADMIN' || user?.role === 'ADMIN';

  const [aba, setAba] = useState<'abertas' | 'fechadas'>('abertas');
  const [mesas, setMesas] = useState<MesaResumo[]>([]);
  const [fechadas, setFechadas] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // T109: o balcão tem chave própria ('balcao'). Antes ele usava null — o mesmo
  // valor de "nada aberto" — então nascia expandido e o clique não fechava.
  const [aberto, setAberto] = useState<string | null>(null);
  const [comandaDetalhe, setComandaDetalhe] = useState<Order | null>(null);
  // T215: fechar a conta usa o modal do sistema (a janela nativa do navegador
  // era o único "feio" da tela) — guarda a comanda escolhida até confirmar.
  const [contaParaFechar, setContaParaFechar] = useState<Order | null>(null);

  /** T216: resumo reaproveitado no fechar conta e no cancelar a comanda */
  const resumoComanda = (c: Order) => [
    c.table_number ? `Mesa ${c.table_number}` : 'Balcão',
    `Cliente: ${c.customer_name}`,
    `${c.items.filter((i) => !i.cancelado).reduce((a, i) => a + i.quantity, 0)} item(ns)`,
    `Total: R$ ${Number(c.total).toFixed(2)}`,
  ];
  const [erro, setErro] = useState('');
  // T141: aviso de sucesso quando a comanda fecha sozinha (último item cancelado)
  const [aviso, setAviso] = useState('');
  // T114: modal de motivo do cancelamento (item ou comanda)
  const [cancelamento, setCancelamento] = useState<
    { tipo: 'item' | 'comanda'; orderId: string; itemId?: string; titulo: string; resumo?: string[] } | null
  >(null);
  // T111: falha de API é estado próprio — nunca pode virar "nenhuma comanda"
  const [erroCarregamento, setErroCarregamento] = useState('');
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);

  const carregarAbertas = async (silencioso = false) => {
    if (!silencioso) setIsLoading(true);
    try {
      const r = await orderService.resumoMesas();
      setMesas(r.data.data);
      setErroCarregamento('');
      setAtualizadoEm(new Date());
    } catch {
      // T111: mantém a última lista válida e avisa que o carregamento falhou
      setErroCarregamento('Não foi possível carregar as comandas');
    } finally {
      if (!silencioso) setIsLoading(false);
    }
  };

  const carregarFechadas = async (silencioso = false) => {
    if (!silencioso) setIsLoading(true);
    try {
      const r = await orderService.findAll({ page: 1, limit: 100 });
      const hoje = new Date().toDateString();
      setFechadas(
        r.data.data.filter((o) => {
          // T144: "fechada hoje" é o dia em que foi FECHADA (paid_at), não o dia
          // em que a comanda foi aberta. Cancelada segue pelo dia de criação.
          const quando = o.status === 'PAID' ? o.paid_at ?? o.created_at : o.created_at;
          return (
            ['PAID', 'CANCELLED'].includes(o.status) &&
            new Date(quando).toDateString() === hoje
          );
        }),
      );
      setErroCarregamento('');
      setAtualizadoEm(new Date());
    } catch {
      setErroCarregamento('Não foi possível carregar as comandas fechadas');
    } finally {
      if (!silencioso) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (aba === 'abertas') {
      carregarAbertas();
    } else if (ehAdmin) {
      carregarFechadas();
    }
  }, [aba, ehAdmin]);

  // T110: a tela se atualiza sozinha a cada 5s (padrão já usado em Pedidos) e
  // pausa enquanto a aba do navegador está oculta.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      if (aba === 'abertas') carregarAbertas(true);
      else if (ehAdmin) carregarFechadas(true);
    }, 5000);
    return () => clearInterval(timer);
  }, [aba, ehAdmin]);

  const cancelarItem = (orderId: string, itemId: string) => {
    // T114: pede o motivo antes de cancelar (fica gravado + vai para auditoria)
    setCancelamento({
      tipo: 'item',
      orderId,
      itemId,
      titulo: 'Cancelar item da comanda',
    });
  };

  const cancelarComanda = (orderId: string) => {
    setCancelamento({
      tipo: 'comanda',
      orderId,
      titulo: 'Cancelar a comanda inteira',
      // T216: devolve estoque e não tem volta — o modal mostra o que sai
      resumo: comandaDetalhe ? resumoComanda(comandaDetalhe) : undefined,
    });
  };

  /** Executa o cancelamento já com o motivo escolhido no modal (T114) */
  const confirmarCancelamento = async (motivo: string) => {
    if (!cancelamento) return;
    const { tipo, orderId, itemId } = cancelamento;
    setErro('');
    try {
      if (tipo === 'item' && itemId) {
        const r = await orderService.cancelarItem(orderId, itemId, motivo);
        // T141: cancelou o último item → a comanda fecha sozinha e o nome
        // volta a ficar livre na mesa (antes ficava aberta em R$ 0,00)
        const fechouSemItens = (r.data.data as { comanda_fechada_sem_itens?: boolean })
          .comanda_fechada_sem_itens;
        if (fechouSemItens) {
          setAviso(
            'Comanda fechada: era o último item. O nome já está livre nesta mesa (a ação ficou no histórico).',
          );
          setComandaDetalhe(null);
        } else {
          setComandaDetalhe(r.data.data);
        }
      } else {
        await orderService.updateStatus(orderId, { status: 'CANCELLED', motivo });
        setComandaDetalhe(null);
      }
      setCancelamento(null);
      await carregarAbertas();
    } catch {
      setErro(
        tipo === 'item' ? 'Não foi possível cancelar o item' : 'Não foi possível cancelar a comanda',
      );
    }
  };

  /** T215: abre o modal de confirmação (com resumo) em vez da janela nativa */
  const fecharConta = (comanda: Order) => {
    setErro('');
    setContaParaFechar(comanda);
  };

  /** Executa o fechamento já confirmado no modal */
  const confirmarFechamentoConta = async () => {
    const comanda = contaParaFechar;
    if (!comanda) return;
    setErro('');
    try {
      await orderService.updateStatus(comanda.id, { status: 'PAID' });
      setContaParaFechar(null);
      setComandaDetalhe(null);
      await carregarAbertas();
    } catch {
      setErro('Não foi possível fechar a conta');
    }
  };

  /** Entregue há muito tempo sem fechar conta: destaque para o garçom */
  const minutosAbertos = (criadoEm: string) =>
    Math.floor((Date.now() - new Date(criadoEm).getTime()) / 60000);

  // Aba "Abertas": o cliente vira 1 bloco só, mesmo com várias comandas na
  // mesma mesa. O status do bloco é o de MAIOR pendência — enquanto existir
  // comanda não concluída, mostra a pendência; só mostra "Entregue" quando
  // não sobrar nada pendente.
  const ORDEM_PENDENCIA = ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'REQUESTED_BILL', 'DELIVERED'];
  const statusDominante = (comandas: Order[]) => {
    let melhor = comandas[0]?.status ?? 'PENDING';
    let idxMelhor = Infinity;
    for (const c of comandas) {
      const idx = ORDEM_PENDENCIA.indexOf(c.status);
      if (idx !== -1 && idx < idxMelhor) {
        idxMelhor = idx;
        melhor = c.status;
      }
    }
    return melhor;
  };
  /**
   * T143: a mesa abre por COMANDA (não por nome) — dois "João" são contas
   * separadas, cada uma com o seu total. Homônimos ganham número para o
   * garçom distinguir na hora de entregar/fechar.
   */
  const comandasDaMesa = (comandas: Order[]) => {
    const quantosPorNome = new Map<string, number>();
    for (const c of comandas) {
      const chave = c.customer_name.trim().toLowerCase();
      quantosPorNome.set(chave, (quantosPorNome.get(chave) ?? 0) + 1);
    }
    const vistos = new Map<string, number>();

    return [...comandas]
      .sort((a, b) => {
        const ia = ORDEM_PENDENCIA.indexOf(a.status);
        const ib = ORDEM_PENDENCIA.indexOf(b.status);
        if (ia !== ib) return ia - ib;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      })
      .map((comanda) => {
        const chave = comanda.customer_name.trim().toLowerCase();
        if ((quantosPorNome.get(chave) ?? 1) === 1) {
          return { comanda, apelido: comanda.customer_name };
        }
        const n = (vistos.get(chave) ?? 0) + 1;
        vistos.set(chave, n);
        return { comanda, apelido: `${comanda.customer_name} (${n})` };
      });
  };

  /**
   * T142: tempo de espera legível — minutos até 1h (antes virava "0h") e
   * "1h20" acima disso.
   */
  const formatarEspera = (minutos: number) => {
    if (minutos < 60) return `${minutos} min`;
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
  };

  /** T142: com conta pedida vai para o topo (prioridade de atendimento) */
  const mesasOrdenadas = [...mesas].sort((a, b) => {
    const contaA = a.comandas.some((c) => c.status === 'REQUESTED_BILL') ? 0 : 1;
    const contaB = b.comandas.some((c) => c.status === 'REQUESTED_BILL') ? 0 : 1;
    if (contaA !== contaB) return contaA - contaB;
    const ia = ORDEM_PENDENCIA.indexOf(statusDominante(a.comandas));
    const ib = ORDEM_PENDENCIA.indexOf(statusDominante(b.comandas));
    if (ia !== ib) return ia - ib;
    return (
      new Date(a.comandas[0]?.created_at ?? 0).getTime() -
      new Date(b.comandas[0]?.created_at ?? 0).getTime()
    );
  });

  // Somente garçom e admin usam esta tela (cozinha é redirecionada)
  if (perfilRestaurante === 'COZINHA' || user?.role === 'WAITER') {
    navigate('/admin/orders', { replace: true });
    return null;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-100">🧾 Comandas</h1>
        <div className="flex items-center gap-2">
          {atualizadoEm && (
            <span className="text-xs text-gray-500 hidden sm:inline">
              atualizado às {atualizadoEm.toLocaleTimeString('pt-BR')}
            </span>
          )}
          <button
            onClick={() => (aba === 'abertas' ? carregarAbertas() : carregarFechadas())}
            className="btn-secondary text-sm"
            title="Atualizar agora"
          >
            🔄 Atualizar
          </button>
          <button
            onClick={() => setAba('abertas')}
            className={`btn-secondary text-sm ${aba === 'abertas' ? 'border-primary-500/60' : 'opacity-50'}`}
          >
            Abertas
          </button>
          {ehAdmin && (
            <button
              onClick={() => setAba('fechadas')}
              className={`btn-secondary text-sm ${aba === 'fechadas' ? 'border-primary-500/60' : 'opacity-50'}`}
            >
              Fechadas hoje
            </button>
          )}
        </div>
      </div>

      {erro && <p className="text-red-400 text-sm mb-4">{erro}</p>}
      {aviso && (
        <div className="card mb-4 flex items-start justify-between gap-3 border-emerald-700/60">
          <p className="text-emerald-300 text-sm">✅ {aviso}</p>
          <button onClick={() => setAviso('')} className="text-gray-400 hover:text-gray-200 text-sm">
            ✕
          </button>
        </div>
      )}

      {/* T111: falha de API tem aviso próprio (nunca vira "sem comandas") */}
      {erroCarregamento && (
        <div className="card mb-4 border border-amber-500/40 bg-amber-900/10 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-amber-300">
            ⚠️ {erroCarregamento} — a lista abaixo pode estar desatualizada.
          </p>
          <button
            onClick={() => (aba === 'abertas' ? carregarAbertas() : carregarFechadas())}
            className="btn-secondary text-sm"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="card text-center py-12">
          <p className="text-gray-400">Carregando comandas...</p>
        </div>
      ) : aba === 'abertas' ? (
        mesas.length === 0 ? (
          erroCarregamento ? (
            <div className="card text-center py-16">
              <p className="text-amber-300 text-xl">⚠️ Não foi possível carregar as comandas</p>
              <p className="text-gray-400 text-sm mt-2">Verifique a conexão e tente novamente.</p>
              <button onClick={() => carregarAbertas()} className="btn-secondary text-sm mt-4">
                Tentar novamente
              </button>
            </div>
          ) : (
            <div className="card text-center py-16">
              <p className="text-gray-400 text-xl">Nenhuma comanda aberta 🌙</p>
            </div>
          )
        ) : (
          <div className="space-y-3">
            {mesasOrdenadas.map((resumo) => {
              const chave = resumo.mesa ? `mesa-${resumo.mesa}` : 'balcao';
              const contaPedida = resumo.comandas.some((c) => c.status === 'REQUESTED_BILL');
              return (
              <div
                key={chave}
                className={`bg-gray-800 rounded-xl shadow-sm border overflow-hidden ${
                  contaPedida ? 'border-amber-500/70 ring-1 ring-amber-500/40' : 'border-gray-700'
                }`}
              >
                <button
                  onClick={() => setAberto(aberto === chave ? null : chave)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-gray-800/50 rounded-lg flex-wrap"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl text-gray-400 whitespace-nowrap">
                      {aberto === chave ? '▾' : '▸'}
                    </span>
                    <span className="text-2xl font-extrabold text-primary-400 whitespace-nowrap">
                      {resumo.mesa ? `MESA ${resumo.mesa}` : 'BALCÃO'}
                    </span>
                    <span className="text-sm text-gray-400 whitespace-nowrap">
                      {resumo.total_comandas}{' '}
                      {resumo.total_comandas === 1 ? 'comanda' : 'comandas'}
                    </span>
                    {/* T142: conta pedida é prioridade de atendimento */}
                    {contaPedida && (
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-gray-900 animate-pulse whitespace-nowrap">
                        🧾 CONTA PEDIDA
                      </span>
                    )}
                  </div>
                  <span className="text-xl font-bold text-primary-400 whitespace-nowrap">
                    R$ {Number(resumo.total_geral).toFixed(2)}
                  </span>
                </button>

                {aberto === chave && (
                  <div className="border-t border-gray-700 px-3 py-3 space-y-2">
                    {comandasDaMesa(resumo.comandas).map(({ comanda, apelido }) => {
                      const contaPedida = comanda.status === 'REQUESTED_BILL';
                      return (
                        <div
                          key={comanda.id}
                          className={`rounded-lg p-3 border ${
                            contaPedida
                              ? 'bg-amber-950/30 border-amber-500/60'
                              : 'bg-gray-800/60 border-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-100 truncate flex-1 min-w-0">
                              {apelido}
                            </p>
                            {/* T142: conta pedida tem selo próprio (era só mais um status) */}
                            {contaPedida ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500 text-gray-900 animate-pulse whitespace-nowrap">
                                🧾 Conta pedida
                              </span>
                            ) : (
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_LABELS[comanda.status].color}`}
                              >
                                {STATUS_LABELS[comanda.status].label}
                              </span>
                            )}
                            <span className="text-sm font-semibold text-gray-300 whitespace-nowrap">
                              R$ {Number(comanda.total).toFixed(2)}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                            <div className="min-w-0">
                              <p className="text-xs text-gray-400">
                                <TempoDeEspera criadoEm={comanda.created_at} /> ·{' '}
                                {comanda.items.length}{' '}
                                {comanda.items.length === 1 ? 'item' : 'itens'}
                              </p>
                              {comanda.status === 'DELIVERED' &&
                                minutosAbertos(comanda.created_at) > 30 && (
                                  <p className="text-xs text-amber-400 mt-0.5">
                                    ⏰ entregue há {formatarEspera(minutosAbertos(comanda.created_at))} — falta fechar
                                  </p>
                                )}
                              {contaPedida && (
                                <p className="text-xs text-amber-300 mt-0.5">
                                  🧾 cliente pediu a conta há {formatarEspera(minutosAbertos(comanda.created_at))}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => setComandaDetalhe(comanda)}
                              className="btn-secondary text-sm whitespace-nowrap sm:shrink-0"
                            >
                              Ver detalhes
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-700 text-sm text-gray-400">
                <th className="p-3">Mesa</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">Total</th>
                <th className="p-3">Status</th>
                <th className="p-3">Fechada em</th>
              </tr>
            </thead>
            <tbody>
              {fechadas.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-gray-400">
                    Nenhuma comanda fechada hoje
                  </td>
                </tr>
              )}
              {fechadas.map((comanda) => (
                <tr key={comanda.id} className="border-b border-gray-800">
                  <td className="p-3 font-bold text-primary-400">
                    {comanda.table_number ? `MESA ${comanda.table_number}` : 'Balcão'}
                  </td>
                  <td className="p-3 font-semibold text-gray-100">{comanda.customer_name}</td>
                  <td className="p-3 font-semibold text-primary-400">
                    R$ {Number(comanda.total).toFixed(2)}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_LABELS[comanda.status].color}`}>
                      {STATUS_LABELS[comanda.status].label}
                    </span>
                  </td>
                  <td className="p-3 text-sm text-gray-400">
                    {new Date(comanda.paid_at ?? comanda.created_at).toLocaleTimeString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {comandaDetalhe && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="card w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h2 className="text-xl font-bold text-gray-100">
                  {comandaDetalhe.customer_name}
                </h2>
                <p className="text-sm text-gray-400">
                  {comandaDetalhe.table_number
                    ? `Mesa ${comandaDetalhe.table_number}`
                    : 'Balcão'}{' '}
                  · {STATUS_LABELS[comandaDetalhe.status].label} ·{' '}
                  <TempoDeEspera criadoEm={comandaDetalhe.created_at} />
                </p>
              </div>
              <button
                onClick={() => setComandaDetalhe(null)}
                className="text-gray-400 hover:text-gray-200 text-xl"
              >
                ✕
              </button>
            </div>

            <div className="border-t pt-3 mb-4 space-y-2">
              {comandaDetalhe.items.map((item) => (
                <div
                  key={item.id}
                  className={`flex justify-between items-center text-sm gap-2 ${
                    item.cancelado ? 'opacity-50' : ''
                  }`}
                >
                  <span className="text-gray-200">
                    {item.quantity}x {item.product?.name}
                    {item.lancado_por === 'garcom' ? (
                      <span className="ml-1 text-xs text-purple-300 bg-purple-900/30 px-1.5 py-0.5 rounded">
                        🧑‍🍳 garçom
                      </span>
                    ) : (
                      <span className="ml-1 text-xs text-gray-500 bg-gray-700/40 px-1.5 py-0.5 rounded">
                        📱 cliente
                      </span>
                    )}
                    {item.cancelado && (
                      <span className="ml-1 text-xs text-red-400 bg-red-900/30 px-1.5 py-0.5 rounded">
                        ✕ cancelado
                      </span>
                    )}
                    {/* T114: motivo e autor do cancelamento ficam visíveis */}
                    {item.cancelado && (
                      <span className="block text-xs text-red-300/80 mt-0.5">
                        motivo: {item.motivo_cancelamento || 'não informado'}
                        {item.cancelado_por ? ` · por ${item.cancelado_por}` : ''}
                      </span>
                    )}
                    {/* T112: a observação do item precisa aparecer na comanda */}
                    {item.notes && (
                      <span className="block text-xs text-amber-300 mt-0.5">📝 {item.notes}</span>
                    )}
                  </span>
                  <span className="text-gray-300">
                    R$ {Number(item.subtotal).toFixed(2)}
                  </span>
                  {!item.cancelado && comandaDetalhe.status !== 'PAID' && (
                    <button
                      onClick={() => cancelarItem(comandaDetalhe.id, item.id)}
                      className="btn-danger text-xs px-2 py-1"
                    >
                      ✕ Cancelar
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="border-t border-gray-700 pt-3 flex justify-between items-center">
              <span className="font-semibold text-gray-200">Total</span>
              <span className="text-2xl font-extrabold text-primary-400">
                R$ {Number(comandaDetalhe.total).toFixed(2)}
              </span>
            </div>

            {comandaDetalhe.status !== 'PAID' && (
              <div className="mt-4 space-y-2">
                {/* T113: disponível em qualquer status (antes só em READY/DELIVERED/conta pedida) */}
                {['PENDING', 'CONFIRMED', 'PREPARING'].includes(comandaDetalhe.status) && (
                  <p className="text-xs text-amber-300">
                    ⚠️ Comanda com item ainda em produção — fechar fecha a conta do que já foi lançado.
                  </p>
                )}
                <button
                  onClick={() => fecharConta(comandaDetalhe)}
                  className="btn-primary w-full"
                >
                  💰 Fechar conta
                </button>
                <button
                  onClick={() => cancelarComanda(comandaDetalhe.id)}
                  className="btn-danger w-full"
                >
                  ✕ Cancelar comanda inteira
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* T215: fechar conta com o modal do sistema (2 cliques com resumo) */}
      {contaParaFechar && (
        <ModalConfirmacao
          titulo="Fechar a conta"
          subtitulo={`${contaParaFechar.table_number ? `Mesa ${contaParaFechar.table_number}` : 'Balcão'} · ${contaParaFechar.customer_name}`}
          resumo={resumoComanda(contaParaFechar)}
          aviso={
            ['PENDING', 'CONFIRMED', 'PREPARING'].includes(contaParaFechar.status)
              ? 'Esta comanda ainda tem item em produção na cozinha.'
              : undefined
          }
          rotuloConfirmar="Fechar conta"
          onConfirmar={confirmarFechamentoConta}
          onCancelar={() => setContaParaFechar(null)}
        />
      )}

      {/* T114: modal do motivo (item ou comanda inteira) */}
      {cancelamento && (
        <ModalMotivoCancelamento
          titulo={cancelamento.titulo}
          // T216: cancelar a comanda inteira devolve estoque e não tem volta —
          // mostra o que está sendo cancelado (o de item já mostrava o resumo)
          resumo={cancelamento.resumo}
          onConfirmar={confirmarCancelamento}
          onCancelar={() => setCancelamento(null)}
        />
      )}
    </div>
  );
}